import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, Box, Typography, IconButton, TextField, Button, Tooltip,
  Select, MenuItem, Chip, Collapse,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import PersonIcon from '@material-ui/icons/Person';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import HomeIcon from '@material-ui/icons/Home';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import BuildIcon from '@material-ui/icons/Build';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import StarIcon from '@material-ui/icons/Star';
import SearchIcon from '@material-ui/icons/Search';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { AuthContext } from '../../App';
import { selectDrivers, createDriver, updateDriver, deleteDriver } from '../../store/slices/driversSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { t } from '../../i18n';
import SkillsPicker from '../common/SkillsPicker';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const MAP_LIBRARIES = ['places'];

// Compact map used inside the Contacto section to show exactly where the
// driver's home is. Loads Google Maps lazily and falls back to a "no map"
// placeholder when the key is missing. Non-interactive (read-only view).
function DriverHomeMap({ lat, lng, addressLabel }) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: MAP_LIBRARIES,
    language: 'pt',
    region: 'PT',
  });
  if (!lat || !lng) return null;
  const center = { lat: Number(lat), lng: Number(lng) };
  if (!isLoaded || !GOOGLE_KEY) {
    return (
      <Box style={{
        height: 180, borderRadius: 10, border: '1px solid #e2e8f0',
        background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#64748b', fontSize: '0.72rem', fontWeight: 600,
      }}>
        📍 {center.lat.toFixed(5)}, {center.lng.toFixed(5)}
      </Box>
    );
  }
  return (
    <Box style={{
      height: 220, borderRadius: 10, overflow: 'hidden',
      border: '1px solid #e2e8f0', position: 'relative',
    }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={16}
        options={{
          // Keep all controls the operator expects on a real map — zoom,
          // pan, street view, satellite toggle, fullscreen. `greedy` lets
          // you pan with the finger/mouse without needing Ctrl.
          gestureHandling: 'greedy',
          zoomControl: true,
          streetViewControl: true,
          mapTypeControl: true,
          fullscreenControl: true,
          clickableIcons: false,
        }}
      >
        <Marker
          position={center}
          title={addressLabel || ''}
          label={{ text: '🏠', fontSize: '16px' }}
        />
      </GoogleMap>
      {/* Footer with the full address for quick reference */}
      {addressLabel && (
        <Box style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '6px 10px',
          background: 'rgba(255,255,255,0.95)',
          borderTop: '1px solid #e2e8f0',
          fontSize: '0.68rem', fontWeight: 600, color: '#334155',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          📍 {addressLabel}
        </Box>
      )}
    </Box>
  );
}

const emptyForm = {
  name: '', email: '', licenseClass: '', tachoCard: '',
  phone: '', homeAddress: '', homeLat: null, homeLng: null,
  skills: [],
};

// Styles mirror the OrderForm visual language so both dialogs feel like the
// same product surface: rounded 16px Paper, off-white content, white section
// cards with collapsible headers, ghost-primary save button.
const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      maxWidth: 780,
      width: '100%',
      overflow: 'hidden',
    },
  },
  title: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', height: 56,
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 60%)',
    borderBottom: '1px solid #fed7aa',
  },
  titleLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  titleAvatar: {
    width: 32, height: 32, borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
    border: '1px solid #fed7aa',
  },
  titleText: { fontWeight: 700, fontSize: '0.95rem', color: '#9a3412' },
  titleSub: { fontSize: '0.68rem', color: '#c2410c', fontWeight: 500 },
  closeBtn: {
    fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 500,
    borderRadius: 8, border: '1px solid #fed7aa', padding: '4px 12px', background: '#fff',
    '&:hover': { background: '#fff7ed' },
  },

  content: {
    padding: '16px 24px 24px',
    overflowY: 'auto',
    maxHeight: 'calc(85vh - 130px)',
    background: '#fafbfc',
  },

  // ─── List view ────────────────────────────────────────────
  listSearch: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: '#fff', border: '1px solid #fed7aa', borderRadius: 10,
    padding: '8px 12px', marginBottom: 12,
    transition: 'border-color 0.15s',
    '&:focus-within': { borderColor: '#f97316' },
  },
  listSearchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: '0.82rem',
    background: 'transparent', color: '#0f172a',
    fontFamily: 'inherit',
  },
  card: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: '#fff', borderRadius: 10,
    border: '1px solid #fed7aa', padding: '12px 14px',
    marginBottom: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      borderColor: '#fdba74',
      boxShadow: '0 2px 8px rgba(249,115,22,0.10)',
    },
  },
  cardAvatar: {
    width: 40, height: 40, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
    border: '1px solid #fed7aa', flexShrink: 0,
  },
  cardAvatarText: {
    fontWeight: 700, fontSize: '0.82rem', color: '#c2410c',
    letterSpacing: '-0.02em',
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardName: {
    fontWeight: 700, fontSize: '0.86rem', color: '#0f172a',
    letterSpacing: '-0.01em',
  },
  cardLine: {
    display: 'flex', alignItems: 'center', gap: 4,
    fontSize: '0.68rem', color: '#64748b',
    marginTop: 1,
  },
  cardMeta: {
    display: 'flex', alignItems: 'center', gap: 10, marginTop: 3,
    flexWrap: 'wrap',
  },
  cardBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    fontSize: '0.62rem', fontWeight: 600, color: '#64748b',
    background: '#f1f5f9', padding: '2px 7px', borderRadius: 999,
  },
  cardBadgeAccent: {
    color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa',
  },
  cardBadgeGreen: {
    color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0',
  },
  cardActions: { display: 'flex', gap: 4, flexShrink: 0 },
  cardIconBtn: {
    width: 28, height: 28,
    '&:hover': { background: '#fff7ed' },
  },

  // ─── Empty state ──────────────────────────────────────────
  empty: {
    textAlign: 'center', padding: '32px 20px',
    background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 12,
    color: '#94a3b8',
  },
  emptyTitle: { fontSize: '0.82rem', fontWeight: 600, color: '#64748b', marginBottom: 4 },
  emptyDesc: { fontSize: '0.72rem', color: '#94a3b8' },

  // ─── Form sections ────────────────────────────────────────
  formHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#fff', borderRadius: 10, border: '1px solid #fed7aa',
    padding: '10px 14px', marginBottom: 10,
  },
  formHeaderTitle: {
    fontWeight: 700, fontSize: '0.82rem', color: '#9a3412',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  backBtn: {
    fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 500,
    borderRadius: 8, padding: '4px 10px',
    '&:hover': { background: '#fff7ed' },
  },

  section: {
    marginBottom: 8,
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #fed7aa',
    // NOTE: deliberately NOT `overflow: hidden` — the address autocomplete
    // dropdown is positioned absolute inside this section's body and would
    // otherwise be clipped by the section's rounded container.
  },
  sectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer', padding: '10px 14px',
    transition: 'background 0.12s',
    '&:hover': { background: '#fff7ed' },
  },
  sectionTitle: {
    fontWeight: 700, fontSize: '0.78rem', color: '#9a3412',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  sectionIcon: {
    width: 22, height: 22, borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#fff7ed', color: '#ea580c',
  },
  sectionBody: { padding: '4px 14px 14px' },

  row: { display: 'flex', gap: 10, marginBottom: 8 },
  field: {
    '& .MuiOutlinedInput-root': { borderRadius: 8, background: '#fafbfc', fontSize: '0.78rem' },
    '& .MuiOutlinedInput-input': { padding: '10px 12px' },
    '& .MuiInputLabel-root': { fontSize: '0.74rem' },
    '& .MuiFormHelperText-root': { fontSize: '0.66rem', marginLeft: 4 },
  },

  // Address autocomplete
  addressWrap: { position: 'relative', flex: 1 },
  suggestions: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    zIndex: 1300, marginTop: 4,
    background: '#fff', border: '1px solid #fed7aa', borderRadius: 10,
    boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
    maxHeight: 220, overflowY: 'auto',
  },
  suggestionItem: {
    padding: '9px 12px', fontSize: '0.76rem', color: '#0f172a',
    cursor: 'pointer', borderBottom: '1px solid #ffedd5',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    '&:hover': { background: '#fff7ed' },
    '&:last-child': { borderBottom: 'none' },
  },
  suggestionSource: {
    fontSize: '0.56rem', fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    background: '#f1f5f9', padding: '2px 6px', borderRadius: 4,
    flexShrink: 0,
  },

  // Vehicle chips
  vehicleChipsRow: {
    display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10,
  },
  vehicleSelectRow: { display: 'flex', gap: 8, alignItems: 'center' },
  miniAddBtn: {
    fontSize: '0.7rem', fontWeight: 600, color: '#fff',
    background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
    borderRadius: 8, textTransform: 'none', minWidth: 'auto',
    padding: '6px 12px',
    '&:hover': { background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' },
    '&.Mui-disabled': { background: '#fed7aa', color: '#fff' },
  },

  error: {
    color: '#dc2626', fontSize: '0.72rem', textAlign: 'center',
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
    padding: '8px 12px', marginTop: 8, fontWeight: 500,
  },

  // ─── Actions bar ──────────────────────────────────────────
  actions: {
    padding: '12px 24px',
    borderTop: '1px solid #fed7aa',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: 10,
    background: '#fff',
  },
  addNewBtn: {
    fontSize: '0.78rem', fontWeight: 600, textTransform: 'none',
    borderRadius: 8, padding: '7px 14px',
    color: '#9a3412', background: '#fff7ed',
    border: '1px solid #fed7aa',
    '&:hover': { background: '#ffedd5' },
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff', fontWeight: 600,
    fontSize: '0.78rem', letterSpacing: '-0.005em',
    textTransform: 'none', borderRadius: 8, padding: '7px 20px',
    boxShadow: '0 2px 6px rgba(249,115,22,0.28)', border: 'none',
    transition: 'background 0.15s',
    '&:hover': { background: 'linear-gradient(135deg, #f97316, #ea580c)', boxShadow: '0 2px 6px rgba(249,115,22,0.28)' },
    '&.Mui-disabled': { background: '#fed7aa', color: '#fff', border: 'none', boxShadow: 'none' },
  },
  cancelBtn: {
    fontWeight: 500, fontSize: '0.78rem', textTransform: 'none',
    borderRadius: 8, color: '#64748b', padding: '7px 14px',
  },
}));

// Small collapsible section — mirrors OrderForm's CollapsibleSection so the
// two dialogs feel identical to operators.
function Section({ title, icon, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const classes = useStyles();
  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHeader} onClick={() => setOpen(!open)}>
        <Typography className={classes.sectionTitle}>
          {icon && <Box className={classes.sectionIcon}>{icon}</Box>}
          {title}
        </Typography>
        {open
          ? <ExpandLessIcon style={{ fontSize: 18, color: '#94a3b8' }} />
          : <ExpandMoreIcon style={{ fontSize: 18, color: '#94a3b8' }} />}
      </Box>
      <Collapse in={open}>
        <Box className={classes.sectionBody}>{children}</Box>
      </Collapse>
    </Box>
  );
}

function initials(name) {
  return (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
}

function ManageDriversDialog({ open, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const drivers = useSelector(selectDrivers);
  const vehicles = useSelector(selectVehicles);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [driverVehicles, setDriverVehicles] = useState({});
  const [addVehicleId, setAddVehicleId] = useState('');
  const [search, setSearch] = useState('');

  // Address autocomplete state
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrFocused, setAddrFocused] = useState(false);
  const addrDebounceRef = useRef(null);

  // Fetch vehicles for all drivers on open
  useEffect(() => {
    if (!open || !token) return;
    async function loadAll() {
      const map = {};
      for (const d of drivers) {
        try {
          const res = await fetch(`/planner-api/drivers/${d.id}/vehicles`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) map[d.id] = await res.json();
        } catch { /* ignore */ }
      }
      setDriverVehicles(map);
    }
    loadAll();
  }, [open, token, drivers]);

  const assignVehicle = useCallback(async (driverId, vehicleId) => {
    try {
      const res = await fetch(`/planner-api/drivers/${driverId}/vehicles`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ vehicleId, isPrimary: !(driverVehicles[driverId]?.length) }),
      });
      if (res.ok) {
        const r2 = await fetch(`/planner-api/drivers/${driverId}/vehicles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r2.ok) {
          const data = await r2.json();
          setDriverVehicles(prev => ({ ...prev, [driverId]: data }));
        }
      }
    } catch { /* ignore */ }
    setAddVehicleId('');
  }, [token, driverVehicles]);

  const removeVehicle = useCallback(async (driverId, vehicleId) => {
    try {
      await fetch(`/planner-api/drivers/${driverId}/vehicles/${vehicleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setDriverVehicles(prev => ({
        ...prev,
        [driverId]: (prev[driverId] || []).filter(v => v.vehicleId !== vehicleId),
      }));
    } catch { /* ignore */ }
  }, [token]);

  function resetAdd() {
    setForm({ ...emptyForm });
    setShowAdd(false);
    setEditId(null);
    setError('');
    setAddrSuggestions([]);
  }

  function upd(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function onHomeAddressChange(val) {
    setForm(f => ({ ...f, homeAddress: val, homeLat: null, homeLng: null }));
    if (addrDebounceRef.current) clearTimeout(addrDebounceRef.current);
    if (!val || val.trim().length < 3) {
      setAddrSuggestions([]);
      return;
    }
    addrDebounceRef.current = setTimeout(async () => {
      try {
        const { geocodeSearchMulti } = await import('../../utils/mapboxApi');
        const results = await geocodeSearchMulti(val.trim());
        setAddrSuggestions(results || []);
      } catch {
        setAddrSuggestions([]);
      }
    }, 350);
  }

  function onHomeAddressSelect(item) {
    setForm(f => ({
      ...f,
      homeAddress: item.name,
      homeLng: item.coordinates?.[0] ?? null,
      homeLat: item.coordinates?.[1] ?? null,
    }));
    setAddrSuggestions([]);
  }

  async function resolveHome() {
    const txt = (form.homeAddress || '').trim();
    if (!txt) return { homeAddress: null, homeLat: null, homeLng: null };
    if (form.homeLat != null && form.homeLng != null) {
      return { homeAddress: txt, homeLat: form.homeLat, homeLng: form.homeLng };
    }
    try {
      const { geocodeSearchMulti } = await import('../../utils/mapboxApi');
      const results = await geocodeSearchMulti(txt);
      if (results && results[0]?.coordinates) {
        return {
          homeAddress: txt,
          homeLng: results[0].coordinates[0],
          homeLat: results[0].coordinates[1],
        };
      }
    } catch { /* fall through */ }
    return { homeAddress: txt, homeLat: null, homeLng: null };
  }

  async function handleAdd() {
    if (!form.name.trim()) return setError('Nome obrigatório');
    try {
      const home = await resolveHome();
      await dispatch(createDriver({
        token,
        data: {
          name: form.name.trim(),
          email: form.email.trim() || null,
          licenseClass: form.licenseClass.trim() || null,
          tachoCard: form.tachoCard.trim() || null,
          phone: form.phone.trim() || null,
          skills: Array.isArray(form.skills) ? form.skills : [],
          ...home,
        },
      })).unwrap();
      resetAdd();
    } catch (err) { setError(err.message); }
  }

  async function handleUpdate(id) {
    try {
      const home = await resolveHome();
      await dispatch(updateDriver({ token, id, data: {
        name: form.name,
        email: form.email || null,
        licenseClass: form.licenseClass || null,
        tachoCard: form.tachoCard || null,
        phone: form.phone || null,
        skills: Array.isArray(form.skills) ? form.skills : [],
        ...home,
      }})).unwrap();
      resetAdd();
    } catch (err) { setError(err.message); }
  }

  async function handleDelete(id) {
    try { await dispatch(deleteDriver({ token, id })).unwrap(); }
    catch (err) { setError(err.message); }
  }

  // Toggle a driver back to available after reporting a breakdown / illness.
  // Backend resets `unavailableReason` automatically when isAvailable=true.
  async function handleRestoreAvailability(id) {
    try {
      await dispatch(updateDriver({ token, id, data: { isAvailable: true } })).unwrap();
    } catch (err) { setError(err.message); }
  }

  function startEdit(d) {
    setEditId(d.id);
    setForm({
      name: d.name || '', email: d.email || '',
      licenseClass: d.licenseClass || '', tachoCard: d.tachoCard || '',
      phone: d.phone || '',
      homeAddress: d.homeAddress || '',
      homeLat: d.homeLat != null ? Number(d.homeLat) : null,
      homeLng: d.homeLng != null ? Number(d.homeLng) : null,
      skills: Array.isArray(d.skills) ? d.skills : [],
    });
    setAddrSuggestions([]);
    setShowAdd(true);
  }

  const lang = 'pt';

  // Filter drivers by search term (name, email, phone, license, tacho)
  const filteredDrivers = search.trim()
    ? drivers.filter(d => {
        const q = search.trim().toLowerCase();
        return [d.name, d.email, d.phone, d.licenseClass, d.tachoCard]
          .some(f => (f || '').toLowerCase().includes(q));
      })
    : drivers;

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} fullWidth>
      {/* Title bar */}
      <Box className={classes.title}>
        <Box className={classes.titleLeft}>
          <Box className={classes.titleAvatar}>
            <PersonIcon style={{ fontSize: 18, color: '#2563eb' }} />
          </Box>
          <Box>
            <Typography className={classes.titleText}>
              {showAdd
                ? (editId ? 'Editar motorista' : 'Novo motorista')
                : t(lang, 'manageDrivers')}
            </Typography>
            <Typography className={classes.titleSub}>
              {showAdd
                ? (editId ? 'Atualizar dados do motorista' : 'Criar novo motorista')
                : `${drivers.length} ${drivers.length === 1 ? 'motorista registado' : 'motoristas registados'}`}
            </Typography>
          </Box>
        </Box>
        <Button size="small" onClick={onClose}
          startIcon={<CloseIcon style={{ fontSize: 14 }} />}
          className={classes.closeBtn}
        >
          Fechar
        </Button>
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        {!showAdd ? (
          <>
            {/* Search bar */}
            {drivers.length > 0 && (
              <Box className={classes.listSearch}>
                <SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} />
                <input
                  className={classes.listSearchInput}
                  placeholder="Pesquisar por nome, email, telefone, carta ou cartão tacógrafo…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <IconButton size="small" onClick={() => setSearch('')} style={{ padding: 2 }}>
                    <CloseIcon style={{ fontSize: 12, color: '#94a3b8' }} />
                  </IconButton>
                )}
              </Box>
            )}

            {/* Empty / no results */}
            {drivers.length === 0 && (
              <Box className={classes.empty}>
                <PersonOutlineIcon style={{ fontSize: 36, color: '#cbd5e1', marginBottom: 6 }} />
                <Typography className={classes.emptyTitle}>Sem motoristas</Typography>
                <Typography className={classes.emptyDesc}>
                  Adicione o primeiro motorista para o começar a atribuir a rotas.
                </Typography>
              </Box>
            )}
            {drivers.length > 0 && filteredDrivers.length === 0 && (
              <Box className={classes.empty}>
                <Typography className={classes.emptyTitle}>Nenhum resultado</Typography>
                <Typography className={classes.emptyDesc}>
                  Nenhum motorista corresponde a "{search}".
                </Typography>
              </Box>
            )}

            {/* Driver cards */}
            {filteredDrivers.map(d => {
              const vhs = driverVehicles[d.id] || [];
              return (
                <Box key={d.id} className={classes.card}>
                  <Box className={classes.cardAvatar}>
                    <Typography className={classes.cardAvatarText}>{initials(d.name)}</Typography>
                  </Box>
                  <Box className={classes.cardBody}>
                    <Typography className={classes.cardName}>{d.name}</Typography>
                    {d.email && (
                      <Box className={classes.cardLine}>
                        <EmailIcon style={{ fontSize: 11, color: '#3b82f6' }} />
                        <span style={{ color: '#3b82f6' }}>{d.email}</span>
                      </Box>
                    )}
                    <Box className={classes.cardMeta}>
                      {d.phone && (
                        <Box className={classes.cardLine}>
                          <PhoneIcon style={{ fontSize: 11, color: '#94a3b8' }} />
                          <span>{d.phone}</span>
                        </Box>
                      )}
                      {d.licenseClass && (
                        <span className={`${classes.cardBadge} ${classes.cardBadgeAccent}`}>
                          <AssignmentIndIcon style={{ fontSize: 11 }} />
                          {d.licenseClass}
                        </span>
                      )}
                      {d.tachoCard && (
                        <span className={classes.cardBadge}>
                          Tacho: {d.tachoCard}
                        </span>
                      )}
                      {d.homeLat != null && d.homeLng != null && (
                        <span className={`${classes.cardBadge} ${classes.cardBadgeGreen}`}>
                          <HomeIcon style={{ fontSize: 11 }} />
                          Morada ✓
                        </span>
                      )}
                      {d.isAvailable === false && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 999,
                          background: '#fef2f2', color: '#b91c1c',
                          border: '1px solid #fecaca',
                          fontSize: '0.6rem', fontWeight: 800,
                        }}
                        title={d.unavailableReason || 'Motorista marcado como indisponível'}>
                          🚨 Indisponível{d.unavailableReason ? ` · ${d.unavailableReason}` : ''}
                        </span>
                      )}
                    </Box>
                    {vhs.length > 0 && (
                      <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                        {vhs.map(v => (
                          <Chip key={v.vehicleId} size="small"
                            icon={<LocalShippingIcon style={{ fontSize: 12 }} />}
                            label={(
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                {v.plate}
                                {v.isPrimary && <StarIcon style={{ fontSize: 10, color: '#f59e0b' }} />}
                              </span>
                            )}
                            style={{
                              fontSize: '0.62rem', height: 22,
                              background: v.isPrimary ? '#dbeafe' : '#f1f5f9',
                              border: v.isPrimary ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                              color: v.isPrimary ? '#1e40af' : '#64748b',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box className={classes.cardActions}>
                    {d.isAvailable === false && (
                      <Tooltip title="Repor disponibilidade — limpa o motivo e volta a aparecer nas atribuições">
                        <Button
                          size="small"
                          onClick={() => handleRestoreAvailability(d.id)}
                          variant="contained"
                          style={{
                            background: '#16a34a', color: '#fff',
                            fontSize: '0.62rem', fontWeight: 800, textTransform: 'none',
                            padding: '3px 10px', minWidth: 0, borderRadius: 8,
                            boxShadow: 'none',
                          }}>
                          ✓ Repor disponibilidade
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => startEdit(d)} className={classes.cardIconBtn}
                        style={{ color: '#64748b' }}>
                        <EditIcon style={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    {d.isLocal ? (
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => handleDelete(d.id)} className={classes.cardIconBtn}
                          style={{ color: '#ef4444' }}>
                          <DeleteIcon style={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Motorista gerido no EcooGPS — só pode ser eliminado no próprio EcooGPS">
                        <span style={{
                          fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.02em',
                          color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa',
                          padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                        }}>EcooGPS</span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </>
        ) : (
          // ─── Form view ────────────────────────────────────
          <>
            {/* Identificação */}
            <Section
              title="Identificação"
              icon={<PersonOutlineIcon style={{ fontSize: 14 }} />}
              defaultOpen
            >
              <Box className={classes.row}>
                <TextField className={classes.field} variant="outlined" size="small" required
                  label={t(lang, 'name')}
                  value={form.name}
                  onChange={e => { upd('name', e.target.value); setError(''); }}
                  disabled={!!(editId && drivers.find(d => d.id === editId)?.externalId)}
                  helperText={editId && drivers.find(d => d.id === editId)?.externalId
                    ? 'Nome gerido no EcooGPS — só se altera lá.' : undefined}
                  style={{ flex: 1 }}
                />
                <TextField className={classes.field} variant="outlined" size="small"
                  label="Email"
                  value={form.email}
                  onChange={e => upd('email', e.target.value)}
                  style={{ flex: 1 }}
                />
              </Box>
            </Section>

            {/* Contacto + Morada */}
            <Section
              title="Contacto e morada"
              icon={<PhoneIcon style={{ fontSize: 14 }} />}
              defaultOpen
            >
              <Box className={classes.row}>
                <TextField className={classes.field} variant="outlined" size="small"
                  label={t(lang, 'phone')}
                  placeholder="Ex.: 912 345 678"
                  value={form.phone}
                  onChange={e => upd('phone', e.target.value)}
                  style={{ flex: 1 }}
                />
              </Box>
              <Box className={classes.row}>
                <Box className={classes.addressWrap}>
                  <TextField className={classes.field} variant="outlined" size="small" fullWidth
                    label="Morada da residência"
                    placeholder="Escreve e escolhe uma sugestão"
                    helperText={
                      form.homeLat != null && form.homeLng != null
                        ? `✓ Coordenadas: ${Number(form.homeLat).toFixed(6)}, ${Number(form.homeLng).toFixed(6)}`
                        : 'Escolhe uma sugestão para guardar coordenadas precisas (ponto de partida/regresso das assistências).'
                    }
                    FormHelperTextProps={{
                      style: form.homeLat != null && form.homeLng != null
                        ? { color: '#16a34a', fontWeight: 600 }
                        : undefined,
                    }}
                    value={form.homeAddress}
                    onChange={e => onHomeAddressChange(e.target.value)}
                    onFocus={() => setAddrFocused(true)}
                    onBlur={() => setTimeout(() => setAddrFocused(false), 200)}
                  />
                  {addrFocused && addrSuggestions.length > 0 && (
                    <Box className={classes.suggestions}>
                      {addrSuggestions.map((s, i) => (
                        <Box key={`${s.source || 'x'}-${i}`}
                          className={classes.suggestionItem}
                          onMouseDown={() => onHomeAddressSelect(s)}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.name}
                          </span>
                          {s.source && (
                            <span className={classes.suggestionSource}>{s.source}</span>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
              {/* Mini-mapa com a morada geocoded para feedback visual imediato */}
              {form.homeLat != null && form.homeLng != null && (
                <Box style={{ marginTop: 10 }}>
                  <DriverHomeMap
                    lat={form.homeLat}
                    lng={form.homeLng}
                    addressLabel={form.homeAddress}
                  />
                </Box>
              )}
            </Section>

            {/* Documentos */}
            <Section
              title="Documentos"
              icon={<AssignmentIndIcon style={{ fontSize: 14 }} />}
              defaultOpen={false}
            >
              <Box className={classes.row}>
                <TextField className={classes.field} variant="outlined" size="small"
                  label={t(lang, 'licenseClass')}
                  placeholder="Ex.: C, C+E"
                  value={form.licenseClass}
                  onChange={e => upd('licenseClass', e.target.value)}
                  style={{ flex: 1 }}
                />
                <TextField className={classes.field} variant="outlined" size="small"
                  label={t(lang, 'tachoCard')}
                  value={form.tachoCard}
                  onChange={e => upd('tachoCard', e.target.value)}
                  style={{ flex: 1 }}
                />
              </Box>
            </Section>

            {/* Competências */}
            <Section
              title="Competências técnicas"
              icon={<BuildIcon style={{ fontSize: 14 }} />}
              defaultOpen
            >
              <SkillsPicker
                value={form.skills}
                onChange={(next) => upd('skills', next)}
                lang={lang}
                emptyLabel="Sem competências atribuídas — escolhe abaixo ou adiciona personalizada"
                accent="#2563eb"
              />
            </Section>

            {/* Veículos (edit mode only) */}
            {editId && (
              <Section
                title="Veículos associados"
                icon={<LocalShippingIcon style={{ fontSize: 14 }} />}
                defaultOpen
              >
                <Box className={classes.vehicleChipsRow}>
                  {(driverVehicles[editId] || []).length === 0 && (
                    <Typography style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      Nenhum veículo associado
                    </Typography>
                  )}
                  {(driverVehicles[editId] || []).map(v => (
                    <Chip key={v.vehicleId} size="small"
                      icon={<LocalShippingIcon style={{ fontSize: 13 }} />}
                      label={(
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          {v.plate}
                          {v.isPrimary && <StarIcon style={{ fontSize: 11, color: '#f59e0b' }} />}
                        </span>
                      )}
                      onDelete={() => removeVehicle(editId, v.vehicleId)}
                      style={{
                        fontSize: '0.7rem', height: 26,
                        background: v.isPrimary ? '#dbeafe' : '#f1f5f9',
                        border: v.isPrimary ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                        color: v.isPrimary ? '#1e40af' : '#475569',
                      }}
                    />
                  ))}
                </Box>
                <Box className={classes.vehicleSelectRow}>
                  <Select variant="outlined" displayEmpty
                    value={addVehicleId}
                    onChange={e => setAddVehicleId(e.target.value)}
                    style={{
                      flex: 1, fontSize: '0.78rem', height: 38,
                      borderRadius: 8, background: '#fafbfc',
                    }}
                  >
                    <MenuItem value="" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                      — Selecionar veículo —
                    </MenuItem>
                    {vehicles
                      .filter(v => !(driverVehicles[editId] || []).some(dv => dv.vehicleId === v.id))
                      .map(v => (
                        <MenuItem key={v.id} value={v.id} style={{ fontSize: '0.78rem' }}>
                          <LocalShippingIcon style={{ fontSize: 13, color: '#3b82f6', marginRight: 6 }} />
                          {v.plate}{v.type ? ` · ${v.type}` : ''}
                        </MenuItem>
                      ))
                    }
                  </Select>
                  <Button size="small" variant="contained" disabled={!addVehicleId}
                    onClick={() => assignVehicle(editId, addVehicleId)}
                    startIcon={<AddIcon style={{ fontSize: 14 }} />}
                    className={classes.miniAddBtn}
                  >
                    Adicionar
                  </Button>
                </Box>
              </Section>
            )}
          </>
        )}

        {error && <Typography className={classes.error}>{error}</Typography>}
      </Box>

      {/* Actions bar */}
      <Box className={classes.actions}>
        {!showAdd ? (
          <>
            <span />
            <Button size="small"
              startIcon={<AddIcon style={{ fontSize: 16 }} />}
              onClick={() => { setEditId(null); setForm({ ...emptyForm }); setShowAdd(true); }}
              className={classes.addNewBtn}
            >
              Adicionar motorista
            </Button>
          </>
        ) : (
          <>
            <Button size="small" onClick={resetAdd} className={classes.cancelBtn}>
              {t(lang, 'cancel')}
            </Button>
            <Button size="small" variant="contained"
              onClick={() => editId ? handleUpdate(editId) : handleAdd()}
              disabled={!form.name.trim()}
              className={classes.saveBtn}
            >
              {editId ? t(lang, 'save') : 'Adicionar motorista'}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
}

export default ManageDriversDialog;
